import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import * as authApi from '@/api/auth.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell } from './AuthShell';

/**
 * Página PÚBLICA de canje del token de reset (/reset-password/:token): fija la
 * clave nueva y lleva al login. Los tokens usados/expirados muestran el error
 * del backend ("solicite uno nuevo").
 */
export default function ResetPassword() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => authApi.resetPassword({ token, password }),
    onSuccess: () => navigate('/login', { replace: true }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo restablecer la clave'),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres');
    if (password !== confirm) return setError('Las contraseñas no coinciden');
    reset.mutate();
  }

  return (
    <AuthShell title="Nueva contraseña" subtitle="Elija su contraseña nueva para entrar al panel.">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Contraseña nueva (mínimo 8 caracteres)"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Input
          label="Repita la contraseña"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {error && (
          <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        <Button type="submit" className="w-full" loading={reset.isPending}>
          Guardar y volver al login
        </Button>
        <p className="text-center text-xs text-slate-400">
          <Link to="/login" className="text-acento-texto hover:underline">
            Volver al inicio de sesión
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
